import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PreviewConversation {
  id: string;
  member_name: string;
  business_name: string;
  latest_message_content: string;
  latest_message_time: string;
  message_count: number;
  member_id: string;
  business_id: string;
  messages: {
    id: string;
    content: string;
    sender_id: string;
    sent_at: string;
  }[];
}

export function usePreviewConversations(communityId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['preview-conversations', communityId],
    enabled: enabled && !!communityId,
    queryFn: async () => {
      if (!communityId) return [];

      // Get conversations for the community with member and business info
      const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select(`
          id,
          created_at,
          last_message_at,
          member_id,
          business_id,
          member:user_profiles!fk_conversations_user_profiles (
            firstname,
            lastname
          ),
          business:businesses!conversations_business_id_fkey (
            name
          )
        `)
        .eq('community_id', communityId)
        .order('last_message_at', { ascending: false })
        .limit(10);

      if (conversationsError) {
        console.error('Error fetching conversations:', conversationsError);
        throw conversationsError;
      }

      if (!conversationsData || conversationsData.length === 0) {
        return [];
      }

      // Get messages for each conversation
      const conversationsWithMessages = await Promise.all(
        conversationsData.map(async (conversation: any) => {
          const { data: messagesData, error: messagesError } = await supabase
            .from('messages')
            .select(`
              id,
              content,
              sender_id,
              sent_at,
              user_profiles!fk_messages_sender_user_profiles (
                firstname,
                lastname
              )
            `)
            .eq('conversation_id', conversation.id)
            .order('sent_at', { ascending: false })
            .limit(5);

          if (messagesError) {
            console.error('Error fetching messages:', messagesError);
            return null;
          }

          const messages = (messagesData || []).map((message: any) => ({
            id: message.id,
            content: message.content,
            sender_id: message.sender_id,
            sent_at: message.sent_at
          }));

          const latestMessage = messages[0];
          const memberName = `${conversation.member?.firstname || ''} ${conversation.member?.lastname || ''}`.trim() || 'Member';
          const businessName = conversation.business?.name || 'Business';

          return {
            id: conversation.id,
            member_name: memberName,
            business_name: businessName,
            latest_message_content: latestMessage?.content || '',
            latest_message_time: latestMessage?.sent_at || conversation.last_message_at,
            message_count: messages.length,
            member_id: conversation.member_id,
            business_id: conversation.business_id,
            messages
          };
        })
      );

      return conversationsWithMessages.filter(Boolean) as PreviewConversation[];
    },
  });
}